import { useCallback, useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  useIonViewWillLeave,
} from "@ionic/react";
import { z } from "zod";
import { invokeEdgeFunction } from "../../lib/supabase";
import { useToast } from "../../hooks/useToast";

const MCP_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mcp`;

const ZAccessToken = z.object({
  uuid: z.string(),
  name: z.string(),
  token_prefix: z.string(),
  last_used_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
  created_at: z.string(),
});
type IAccessToken = z.infer<typeof ZAccessToken>;

const ZCreatedAccessToken = ZAccessToken.extend({ token: z.string() });
const ZErrorBody = z.object({ error: z.string() });

const EXPIRY_OPTIONS = [
  { label: "30 days", value: "30" },
  { label: "90 days", value: "90" },
  { label: "1 year", value: "365" },
] as const;

const ZExpiryValue = z.enum(["30", "90", "365"]);
type IExpiryValue = z.infer<typeof ZExpiryValue>;

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function tokenStatus(token: IAccessToken): "active" | "revoked" | "expired" {
  if (token.revoked_at) return "revoked";
  if (token.expires_at && new Date(token.expires_at) <= new Date()) return "expired";
  return "active";
}

async function readError(response: Response): Promise<string> {
  try {
    return ZErrorBody.parse(await response.json()).error;
  } catch {
    return `Request failed (${response.status})`;
  }
}

export default function AiAssistantsPage() {
  const showToast = useToast((s) => s.showToast);

  const [tokens, setTokens] = useState<IAccessToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState<IExpiryValue>("90");
  const [isCreating, setIsCreating] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const loadTokens = useCallback(async () => {
    try {
      const response = await invokeEdgeFunction("access-tokens", { method: "GET" });
      if (!response.ok) throw new Error(await readError(response));
      setTokens(z.array(ZAccessToken).parse(await response.json()));
    } catch (error) {
      console.error("Error loading access tokens:", error);
      showToast("Could not load access tokens", "danger");
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  // Ionic keeps the page mounted after navigating away; never let the plaintext token reappear.
  useIonViewWillLeave(() => {
    setNewToken(null);
  });

  const copy = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(message, "success");
    } catch {
      showToast("Copy failed — select and copy it manually", "warning");
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setIsCreating(true);
    try {
      const response = await invokeEdgeFunction("access-tokens", {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          expires_in_days: Number(expiry),
        }),
      });
      if (!response.ok) throw new Error(await readError(response));
      const created = ZCreatedAccessToken.parse(await response.json());
      setNewToken(created.token);
      setName("");
      await loadTokens();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not create token";
      showToast(message, "danger");
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevoke = async (tokenId: string) => {
    try {
      const response = await invokeEdgeFunction(`access-tokens/${tokenId}`, { method: "DELETE" });
      if (!response.ok) throw new Error(await readError(response));
      showToast("Token revoked", "success");
      await loadTokens();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not revoke token";
      showToast(message, "danger");
    } finally {
      setConfirmRevoke(null);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>AI Assistants</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="min-h-screen bg-background">
          <div className="max-w-lg mx-auto px-6 py-6 space-y-6">
            {/* Server URL */}
            <div className="bg-surface rounded-xl p-6 border border-border space-y-3">
              <h2 className="text-foreground text-lg font-semibold">Connect Claude or ChatGPT</h2>
              <p className="text-muted-foreground/70 text-sm">
                Add Tidy as a connector (MCP server) in Claude or ChatGPT using this URL. You'll be asked to
                sign in to Tidy and approve access. The assistant can then see and manage your households
                with the same permissions you have.
              </p>
              <div className="flex items-center gap-2">
                <code
                  data-testid="mcp-url"
                  className="flex-1 min-w-0 truncate bg-surface-raised text-foreground text-xs px-3 py-2 rounded"
                >
                  {MCP_URL}
                </code>
                <button
                  onClick={() => copy(MCP_URL, "Server URL copied!")}
                  className="bg-surface-raised hover:bg-surface text-foreground px-3 py-2 rounded text-sm font-medium transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>

            {/* Create token */}
            <div className="bg-surface rounded-xl p-6 border border-border space-y-3">
              <h2 className="text-foreground text-lg font-semibold">Personal access tokens</h2>
              <p className="text-muted-foreground/70 text-sm">
                For tools that can't sign in with a browser (Claude Code, the OpenAI API, scripts), create a
                token and send it as <code>Authorization: Bearer &lt;token&gt;</code>. Anyone with the token
                can act as you, so keep it secret.
              </p>

              {newToken && (
                <div className="border border-yellow-500/50 bg-yellow-500/10 rounded-lg p-3 space-y-2">
                  <p className="text-foreground text-sm font-medium">
                    Copy your new token now — you won't be able to see it again.
                  </p>
                  <code
                    data-testid="new-access-token"
                    className="block break-all bg-surface-raised text-foreground text-xs px-3 py-2 rounded"
                  >
                    {newToken}
                  </code>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copy(newToken, "Token copied!")}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
                    >
                      Copy token
                    </button>
                    <button
                      onClick={() => setNewToken(null)}
                      className="flex-1 bg-surface-raised hover:bg-surface text-foreground px-3 py-2 rounded text-sm font-medium transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}

              <label className="block text-sm text-muted-foreground" htmlFor="token-name">
                Token name
              </label>
              <input
                id="token-name"
                type="text"
                value={name}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Claude Code on my laptop"
                className="w-full bg-surface-raised text-foreground px-3 py-2 rounded border border-border"
              />
              <label className="block text-sm text-muted-foreground" htmlFor="token-expiry">
                Expires
              </label>
              <select
                id="token-expiry"
                value={expiry}
                onChange={(e) => {
                  const parsed = ZExpiryValue.safeParse(e.target.value);
                  if (parsed.success) setExpiry(parsed.data);
                }}
                className="w-full bg-surface-raised text-foreground px-3 py-2 rounded border border-border"
              >
                {EXPIRY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                onClick={handleCreate}
                disabled={isCreating || !name.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-surface-raised text-white px-4 py-3 rounded transition-colors font-medium"
              >
                {isCreating ? "Creating..." : "Create token"}
              </button>
            </div>

            {/* Token list */}
            <div className="bg-surface rounded-xl border border-border overflow-hidden divide-y divide-border">
              {isLoading ? (
                <div className="p-4 text-muted-foreground/70 text-sm">Loading tokens...</div>
              ) : tokens.length === 0 ? (
                <div className="p-4 text-muted-foreground/70 text-sm">No tokens yet.</div>
              ) : (
                tokens.map((token) => {
                  const status = tokenStatus(token);
                  return (
                    <div key={token.uuid} className="p-4 space-y-1" data-testid="access-token-row">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-foreground font-medium truncate">{token.name}</span>
                        {status === "active" ? (
                          confirmRevoke === token.uuid ? (
                            <div className="flex gap-2 shrink-0">
                              <button
                                onClick={() => handleRevoke(token.uuid)}
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setConfirmRevoke(null)}
                                className="bg-surface-raised hover:bg-surface text-foreground px-3 py-1 rounded text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmRevoke(token.uuid)}
                              className="text-red-400 hover:text-red-300 text-sm shrink-0"
                            >
                              Revoke
                            </button>
                          )
                        ) : (
                          <span className="text-muted-foreground/70 text-xs uppercase shrink-0">{status}</span>
                        )}
                      </div>
                      <div className="text-muted-foreground/70 text-xs">
                        <code>{token.token_prefix}…</code> · created {formatDate(token.created_at)} · last used{" "}
                        {formatDate(token.last_used_at)} · expires{" "}
                        {token.expires_at ? formatDate(token.expires_at) : "never"}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
